import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { FaArrowLeft, FaCircleCheck } from 'react-icons/fa6';
import { useAuth } from '../lib/auth';
import { fetchJSON } from '../lib/api';

// Fetch the Stripe publishable key from our own backend at runtime — the key
// is public, but CRA bakes REACT_APP_ vars at compile time and Replit secrets
// aren't available until the server starts.
const stripePromise = fetch('/api/checkout/config')
  .then((r) => r.json())
  .then(({ publishableKey }) => loadStripe(publishableKey));

export default function Checkout() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [sessionError, setSessionError] = useState(null);
  const [alreadyEnrolled, setAlreadyEnrolled] = useState(null);
  const [enrollmentChecked, setEnrollmentChecked] = useState(false);

  useEffect(() => {
    document.title = 'Complete your purchase — Learning Outcomes Academy';
    return () => { document.title = 'Learning Outcomes Academy'; };
  }, []);

  // Pre-flight: if the user is already signed in and enrolled, show the
  // "already have access" screen before Stripe loads at all.
  useEffect(() => {
    if (authLoading) return;
    if (!user) { setEnrollmentChecked(true); return; }
    fetchJSON('/api/checkout/enrollment?course_slug=ai-for-parents')
      .then((d) => {
        if (d.enrolled) setAlreadyEnrolled(`/courses/ai-for-parents/module-1/intro`);
      })
      .catch(() => {})
      .finally(() => setEnrollmentChecked(true));
  }, [user, authLoading]);

  // EmbeddedCheckoutProvider calls this to get the clientSecret.
  // Uses fetchJSON so the Authorization: Bearer header is included in dev,
  // ensuring req.user is populated and the server-side duplicate check fires.
  const fetchClientSecret = useCallback(async () => {
    try {
      const data = await fetchJSON('/api/checkout/embedded-session', { method: 'POST' });
      if (!data.clientSecret) {
        const msg = 'Missing client secret from server.';
        setSessionError(msg);
        throw new Error(msg);
      }
      return data.clientSecret;
    } catch (err) {
      if (err.message === 'You already have access to this course.') {
        setAlreadyEnrolled(`/courses/ai-for-parents/module-1/intro`);
      } else {
        setSessionError(err.message || 'Something went wrong. Please try again.');
      }
      throw err;
    }
  }, []);

  return (
    <div className="w-full bg-bone min-h-screen">
      <div className="container-page pt-10 pb-20 max-w-3xl">
        {/* Back link + order summary */}
        <div className="mb-8">
          <Link
            to="/courses/ai-for-parents"
            className="inline-flex items-center gap-2 text-sm text-slate2 hover:text-ink transition-colors"
          >
            <FaArrowLeft className="text-xs" />
            Back to course
          </Link>
          <div className="mt-5">
            <div className="text-[11px] tracking-[0.28em] uppercase text-brand-aubergine mb-1">
              Secure checkout
            </div>
            <h1 className="font-display text-2xl lg:text-3xl tracking-tighter2 text-ink">
              Generative AI for Parents
            </h1>
            <div className="flex items-baseline gap-2 mt-2 flex-wrap">
              <span className="font-display text-2xl text-ink">CA$14.99</span>
              <span className="text-base font-medium text-ink/50 line-through">CA$34.99</span>
              <span className="text-xs bg-brand-aubergine/10 text-brand-aubergine px-2 py-0.5 rounded-full font-medium">
                Early Access — 57% off
              </span>
            </div>
            <p className="text-xs text-slate2 mt-1">
              One-time purchase · Lifetime access
            </p>
          </div>
        </div>

        {/* Already enrolled — don't show the payment form at all */}
        {alreadyEnrolled && (
          <div className="rounded-2xl border border-brand-deep/20 bg-brand-sage/10 p-8 text-center">
            <FaCircleCheck className="mx-auto text-brand-deep text-3xl mb-4" />
            <div className="font-display text-xl text-brand-deep">You already have access</div>
            <p className="mt-3 text-slate2 text-sm leading-relaxed">
              Your account already has a paid enrolment in this course — no need to purchase again.
            </p>
            <button
              type="button"
              onClick={() => navigate(alreadyEnrolled)}
              className="btn-primary mt-6 inline-flex"
            >
              Go to my course
            </button>
          </div>
        )}

        {/* Session-creation error (shown if fetchClientSecret throws) */}
        {!alreadyEnrolled && sessionError && (
          <div className="mb-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {sessionError}{' '}
            <button
              type="button"
              className="underline"
              onClick={() => { setSessionError(null); window.location.reload(); }}
            >
              Try again
            </button>
          </div>
        )}

        {/* Embedded Stripe Checkout — hide once we know the user is already enrolled.
            Also wait for the enrollment pre-flight so we don't flash Stripe then swap it. */}
        {!alreadyEnrolled && enrollmentChecked && (
          <div className="rounded-2xl overflow-hidden border border-ink/10 bg-white shadow-soft">
            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={{ fetchClientSecret }}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        )}

        <p className="mt-6 text-center text-xs text-slate2">
          Payment processed securely by{' '}
          <span className="font-medium text-ink">Stripe</span>.
          Your card details never touch our servers.
        </p>
      </div>
    </div>
  );
}
